import { CheckIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { GlobalConfigType } from 'actions'
import { MedianPriceLink } from 'components/MedianPriceLink'
import {
  type InputType,
  FieldOrigin,
  IVisibleProp,
  MultiPropertyFields,
  rentTaxInsuranceFields,
  sandBoxNotEditableFields,
  vacantOptions,
} from 'config'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { combinedPropertyMonthlyExpense } from 'services/pdfs/utils'
import { Select } from 'stories/components'
import { openAuditLog } from 'utils'
import { isReadOnlyByEditPermission, isViewOnlyByLogic } from 'utils/loan'
import { RenderInput } from 'utils/RenderInput'

import { useAmountEntryFrequency } from './AmountEntryFrequency'
import type {
  ILoan,
  ILoanDetail,
  ILoanProcess,
  IMultipleProperty,
  IPriceAdjustment,
  IPropertyChangedJson,
} from './interfaces'
import { validatePriceAdjustment } from './Logic'

interface IProps {
  targetIndex: number
  loan: ILoan
  propertyData: IMultipleProperty
  loanDetail: ILoanDetail
  loanProcessData: ILoanProcess
  visibleFields: string[]
  loanFields: Record<string, InputType>
  isNewLoan: boolean
  isPool: boolean
  brokerCanNotEditDetail: boolean
  sandBoxMode: boolean
  canEditLoanDetail: boolean
  exceptionApproved: boolean
  canPriceAdjust: boolean
  isBorrower?: boolean
  anytimeCanEditLoanDetail: boolean | undefined
  changedPropertyDetailJson: IPropertyChangedJson[]
  priceAdjustmentObject: Record<string, IPriceAdjustment>
  onChangeInputFields: (key: string, e: any) => void
  onBlur: (key: string) => void
  onRemove: () => void
}

export const MultiPropertyInputs = (props: IProps) => {
  const {
    targetIndex,
    loan,
    loanFields,
    propertyData,
    loanDetail,
    loanProcessData,
    visibleFields,
    isNewLoan,
    isPool,
    brokerCanNotEditDetail,
    changedPropertyDetailJson,
    priceAdjustmentObject,
    sandBoxMode,
    canEditLoanDetail,
    exceptionApproved,
    anytimeCanEditLoanDetail,
    canPriceAdjust,
    isBorrower = false,
    onChangeInputFields,
    onBlur,
    onRemove,
  } = props

  const [tbdUpdateTry, setTBDUpdateTry] = useState(0)
  const { frequencySelect, entryTitle, entryValue, onChangeEntryValue } = useAmountEntryFrequency(onChangeInputFields)

  const { minTotalConstructionFinanced } = loanDetail.loanGlobalSettings

  useEffect(() => {
    if (tbdUpdateTry > 0) onBlur('subjectPropertyAddressTBD')
  }, [tbdUpdateTry])

  const { fieldProperties } = useSelector((state: any) => ({
    fieldProperties:
      ((state.globalConfigReducer[GlobalConfigType.LoanFields] || {})?.properties?.[FieldOrigin.LoanStructure]
        ?.additionalInputs?.additionalProperties as any) || ({} as Record<string, IVisibleProp>),
  }))

  // const allPropertyFields = useMemo(
  //   () => [
  //     ...MultiPropertyFields,
  //     ...Object.keys(loanFields).filter((key) => isCustomFieldKey(key) && !!fieldProperties[key]?.linkedField),
  //   ],
  //   [loanFields, fieldProperties],
  // )

  const hideCancelButton = useMemo(() => {
    if (Number(loanDetail.rateData?.price) > 0 && isBorrower) return true
    if (sandBoxMode) return false
    else {
      if (exceptionApproved && !anytimeCanEditLoanDetail) return true
      if (canEditLoanDetail) return false
      else return true
    }
  }, [loanDetail, isBorrower, sandBoxMode, exceptionApproved, anytimeCanEditLoanDetail, canEditLoanDetail])

  const disableLoanDetailField = (key: string) => {
    if (Number(loanDetail.rateData?.price) > 0 && isBorrower) return true
    if (key === 'estimatedCreditScore' && loanDetail.submitToLoanSetup === 0) return false

    if (sandBoxMode) {
      if (sandBoxNotEditableFields.indexOf(key) !== -1) return true
      return false
    } else {
      if (exceptionApproved && !anytimeCanEditLoanDetail) return true
      if (canEditLoanDetail) return false
      return true
    }
  }

  const onChangeTBD = async () => {
    await onChangeInputFields('subjectPropertyAddressTBD', !propertyData?.subjectPropertyAddressTBD)
    setTimeout(() => {
      setTBDUpdateTry(tbdUpdateTry + 1)
    }, 300)
  }

  const onShowHistory = async (key: string) => {
    const options = {
      table: 'Loan',
      field: loanFields[key].title,
      fieldLabel: loanFields[key].title,
      keys: {
        field: key,
        loanNumber: loanDetail.loanNumber,
        propertyId: propertyData.id,
        propertyIndex: targetIndex,
      },
    }
    openAuditLog(options)
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3 rounded border border-solid border-gray-200 shadow1 p-2 justify-around">
      <div className="col-span-2 flex justify-between -mb-2">
        <div className="text-sm font-variation-settings-600">Property {targetIndex + 2}</div>
        {!hideCancelButton && (
          <div
            className="w-fit p-1 transition-all duration-200 hover:cursor-pointer hover-shadow1 rounded"
            onClick={onRemove}
          >
            <TrashIcon className="w-4 h-4 hover:cursor-pointer text-red-700" />
          </div>
        )}
      </div>
      {MultiPropertyFields.map((key, index) => {
        if (!visibleFields.includes(key)) return <></>
        let rlt = []
        let hideError = false
        const targetValue = (propertyData as any)[key]
        let cn = loanFields[key]?.length === 1 ? 'md:col-span-2' : 'md:col-span-1'
        let tooltip = fieldProperties[key] && fieldProperties[key].tooltip ? fieldProperties[key].tooltip : ''
        let additionalElements = null
        let disabled = false
        if (!loanFields[key]) {
          console.log('Unknown Field', key)
          return null
        }
        let prefix = loanFields[key]?.prefix
        let readOnly = loanFields[key]?.readOnly
        if (!priceAdjustmentObject[key] && key === 'constructionReserve' && minTotalConstructionFinanced === 100)
          readOnly = true
        if (isViewOnlyByLogic(fieldProperties[key]?.viewOnlyLogic)) readOnly = true
        if (!readOnly && isReadOnlyByEditPermission(fieldProperties[key]?.editPermissionAccountTypes)) readOnly = true
        if (brokerCanNotEditDetail || exceptionApproved) {
          const changedData = changedPropertyDetailJson.find((item) => item.key === key)
          if (!!changedData) {
            cn += ' ring-2 ring-red-400 rounded-t'
            const changeString = `${changedData.origin} -> ${changedData.changed}`
            if (tooltip) tooltip = `${tooltip}<br/>${changeString}`
            else tooltip = changeString
          }
        }

        if (key === 'subjectPropertyAddress') {
          additionalElements = (
            <div className="flex items-center gap-2">
              <MedianPriceLink address={propertyData?.subjectPropertyAddress} />
              <div className="flex items-center gap-0.5 hover:cursor-pointer" onClick={onChangeTBD}>
                {!!propertyData?.subjectPropertyAddressTBD ? (
                  <CheckIcon className="w-4 h-4 text-shade-blue" />
                ) : (
                  <XMarkIcon className="w-4 h-4 text-red-700" />
                )}
                <span className="text-[12px]">TBD</span>
              </div>
            </div>
          )
          loanFields[key].required = !propertyData?.subjectPropertyAddressTBD
        }
        if (rentTaxInsuranceFields.includes(key)) additionalElements = frequencySelect(key)
        if (key === 'vacant') {
          prefix = propertyData.vacantIsPercentage ? '%' : ''
          additionalElements = (
            <span onClick={(event) => event.stopPropagation()}>
              <Select
                id={`${key}-vacant`}
                size={3}
                className="-mb-4 w-fit"
                borderClassName="!pr-5 !text-[11px] !text-slate-500 leading-[16px] rounded hover:cursor-pointer"
                options={vacantOptions}
                value={propertyData.vacantIsPercentage ? 'Percentage' : 'Number'}
                onChange={(value) => onChangeInputFields('vacantIsPercentage', value === 'Percentage' ? true : false)}
                onBlur={() => onBlur('vacantIsPercentage')}
              />
            </span>
          )
        }
        if (key !== 'estimatedDscr')
          rlt.push(
            <div key={index} className={cn}>
              <RenderInput
                input={{
                  ...loanFields[key],
                  title: entryTitle(key, loanFields[key].title),
                  error:
                    hideError ||
                    (validatePriceAdjustment(priceAdjustmentObject[key], key, targetValue) && targetValue !== '')
                      ? ''
                      : loanFields[key].error,
                  value: entryValue(key, targetValue ?? ''),
                  prefix,
                  history: !isNewLoan && !isPool,
                  disabled: (disableLoanDetailField(key) ? !canPriceAdjust : false) || disabled,
                  tooltip,
                  additionalElements: isBorrower ? null : additionalElements,
                  readOnly,
                }}
                Key={key}
                onChange={onChangeEntryValue}
                onBlur={onBlur}
                showHistory={onShowHistory}
              />
            </div>,
          )

        if (key === 'estimatedDscr') {
          const pitis = combinedPropertyMonthlyExpense(loan, propertyData, loanProcessData)
          if (pitis['dscr'] !== null) {
            let tooltip = `${propertyData.proposedMonthlyRent} = (Rent) <br/>/<br/> ${pitis['sum']} = (Mortgage Payment:${pitis['mortgagePayment']} + Insurance + Taxes + HoaDues + <br/> Additional Lien Payment)`
            let classNames = 'md:col-span-1'
            if (brokerCanNotEditDetail || exceptionApproved) {
              const dscrChanged = changedPropertyDetailJson.find((item) => item.key === 'dscr')
              if (!!dscrChanged) {
                classNames += ' ring-2 ring-red-400 rounded-t'
                const changeString = `${dscrChanged.origin} -> ${dscrChanged.changed}`
                if (tooltip) tooltip = `${tooltip}<br/><br/>${changeString}`
                else tooltip = changeString
              }
            }
            rlt.push(
              <div key={`monthlyPayment-${index}`} className={classNames}>
                <RenderInput
                  input={{
                    title: 'Monthly PITIA Payment',
                    inputType: 'text',
                    prefix: '$',
                    value: pitis['sum'],
                    readOnly: true,
                  }}
                  Key={'monthlyPayment'}
                  onChange={() => {}}
                />
              </div>,
            )
            rlt.push(
              <div key={`calculatedDSCR-${index}`} className={classNames}>
                <RenderInput
                  input={{
                    title: 'Calculated DSCR',
                    inputType: 'text',
                    prefix: '%',
                    tooltip,
                    value: pitis['dscr'],
                    readOnly: true,
                  }}
                  Key={'estimatedDscr'}
                  onChange={() => {}}
                />
              </div>,
            )
          }
        }
        return rlt
      })}
    </div>
  )
}
